<template>
  <div>
    <div v-on-click-outside="onClickOutside" class="relative">
      <svg class="mx-auto" ref="svgRef"></svg>
      <div
        v-if="selected"
        class="pointer-events-none absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center"
        :style="{ color: getColor(selected.color, 700) }"
      >
        <div
          class="w-fit pointer-events-auto cursor-pointer flex flex-col items-center justify-center"
          @click="emit('centerClicked', selected)"
        >
          <div class="font-bold text-center">
            {{ selected.name }}
          </div>
          <div class="opacity-70">
            {{ showPercentage ? selected.value.toLocaleString('en-US') : selected.percentageStr
            }}{{ showPercentage ? '' : '%' }}
          </div>
        </div>
      </div>
      <div
        v-if="!data.length && emptyMessage"
        class="pointer-events-none absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center"
      >
        <div class="text-center font-medium text-gray-500 italic" style="max-width: 35%">
          {{ emptyMessage }}
        </div>
      </div>
    </div>
    <div class="flex justify-center items-center text-gray-700 text-sm">
      <div
        v-for="d of data"
        class="flex items-center"
        @click="legendClicked(d)"
        :class="{ 'opacity-30': selected && selected.name !== d.name }"
      >
        <div
          v-if="d.value"
          class="w-4 h-4 rounded-full shadow-gray-500 shadow border-2 border-white mr-1 ml-2"
          :style="{ backgroundColor: getColor(d.color) }"
        ></div>
        <div v-if="d.value" class="mr-2">{{ d.name }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as d3 from 'd3';
import { computed, onMounted, ref, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import * as colors from '../../theme/colors.json';

const svgRef = ref();
const emit = defineEmits<{
  (e: 'centerClicked', val: Datum): void;
}>();

export type PieColor = 'gray' | 'blue' | 'cyan' | 'green' | 'red' | 'violet' | 'indigo' | 'yellow';

export interface Datum {
  name: string;
  color: PieColor;
  value: number;
  percentage?: number;
  percentageStr?: string;
}

let updateChart: (() => void) | undefined;
let highlightPieSection: ((id: string) => void) | undefined;
let unhighlightPieSection: (() => void) | undefined;
const props = defineProps<{
  data: Datum[];
  showPercentage?: boolean;
  emptyMessage?: string;
}>();

const selected = ref<Datum | undefined>();
const data = computed(() => {
  const data: Datum[] = [];
  const sum = props.data.reduce((sum, d) => sum + d.value, 0);
  for (const d of props.data) {
    const percentage = (d.value / sum || 0) * 100;
    data.push({
      ...d,
      percentage,
      percentageStr: percentage.toFixed(percentage < 1 ? 1 : 0),
    });
  }
  return data;
});

function getColor(color: PieColor, level: number = 500) {
  return (colors as any)[color][level];
}

function legendClicked(d: Datum) {
  if (selected.value && selected.value.name === d.name) {
    selected.value = undefined;
    unhighlightPieSection?.();
  } else {
    selected.value = d;
    highlightPieSection?.(d.name);
  }
}

watch(
  () => [data.value, props.showPercentage],
  () => {
    if (updateChart) {
      updateChart();
    }
  }
);

onMounted(() => {
  const width = 500;
  const height = Math.min(width, 500);
  const radius = Math.min(width, height) / 2;

  const donutWidth = 0.4;
  const donutGrowFactor = 0.04;
  const animationDuration = 200;

  const arc = d3
    .arc()
    .innerRadius(radius * (0.9 - donutGrowFactor - donutWidth))
    .outerRadius(radius * (0.9 - donutGrowFactor));

  const selectedArc = d3
    .arc()
    .innerRadius(radius * (0.9 - donutGrowFactor * 2 - donutWidth))
    .outerRadius(radius * 0.9);

  const pie = d3
    .pie<Datum>()
    .sort(null)
    .value((d) => d.value);

  const svg = d3
    .select(svgRef.value)
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [-width / 2, -height / 2, width, height])
    .attr('style', 'max-width: 100%; height: auto;');

  const dropShadow = svg.append('g').attr('filter', 'url(#blurFilter)');

  dropShadow
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('x', '-' + width / 2)
    .attr('y', '-' + height / 2)
    .attr('fill', 'white');

  dropShadow
    .append('path')
    .attr('fill', `rgba(0,0,0,0.2`)
    .attr('d', arc({ startAngle: 0, endAngle: 2 * Math.PI, innerRadius: 0, outerRadius: 1 }));

  svg
    .append('g')
    .append('path')
    .attr('fill', colors.gray[300])
    .attr('d', arc({ startAngle: 0, endAngle: 2 * Math.PI, innerRadius: 0, outerRadius: 1 }));

  const defs = svg.append('defs');
  defs
    .append('filter')
    .attr('id', 'blurFilter')
    .attr('x', 0)
    .attr('y', 0)
    .append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', 20);

  for (const [key, color] of Object.entries(colors)) {
    if (color[300] && color[500]) {
      const grad = defs
        .append('radialGradient')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', '50%')
        .attr('id', 'grad-' + key);
      grad.append('stop').attr('offset', '0%').style('stop-color', color[300]);
      grad.append('stop').attr('offset', '100%').style('stop-color', color[500]);
    }
  }

  const getPieArcId = (index: number) => `pie-arc-${index}`;
  const getIndexFromPieArcId = (id: string) => {
    const match = /pie-arc-(\d+)/gi.exec(id);
    if (match?.[1]) {
      const index = +match[1];
      if (!isNaN(index)) {
        return index;
      }
    }
    return -1;
  };

  highlightPieSection = (name: string) => {
    const index = data.value.findIndex((d) => d.name === name);
    const id = getPieArcId(index);
    svg
      .selectAll(`.pie-arc:not(#${id})`)
      .transition()
      .duration(animationDuration)
      .attr('d', (d) => arc(d as any))
      .attr('opacity', 0.5);
    svg
      .select(`#${id}`)
      .transition()
      .duration(animationDuration)
      .attr('d', (d) => selectedArc(d as any))
      .attr('opacity', 1);
  };

  unhighlightPieSection = () => {
    svg
      .selectAll(`.pie-arc`)
      .transition()
      .duration(animationDuration)
      .attr('d', (d) => arc(d as any))
      .attr('opacity', 1);
  };

  updateChart = () => {
    svg
      .selectAll('.pie-arc')
      .data(pie(data.value))
      .join('path')
      .on('click', (e) => {
        const index = getIndexFromPieArcId(e.target.id);
        if (selected.value && selected.value.name == data.value[index]?.name) {
          selected.value = undefined;
          unhighlightPieSection?.();
        } else if (data.value[index]) {
          selected.value = data.value[index];
          highlightPieSection?.(selected.value.name);
        }
      })
      .attr('class', 'pie-arc')
      .attr('id', (d) => getPieArcId(d.index))
      .attr('fill', (d) => `url(#grad-${d.data.color})`)
      .attr('fill', (d) => colors[d.data.color]?.['500'])
      .attr('d', arc as any)
      .attr('opacity', 1)
      .attr('stroke', 'white')
      .attr('stroke-width', 3);

    const textElements = svg
      .selectAll('text')
      .data(pie(data.value))
      .join('text')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 26)
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .attr('transform', (d) => `translate(${arc.centroid(d as any)})`)
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .attr('stroke', (d) => (colors as any)[d.data.color][700])
      .attr('stroke-width', 3)
      .attr('opacity', (d) => (d.value ? '1' : '0'))
      .attr('paint-order', 'stroke');

    if (!props.showPercentage) {
      textElements.text((d) => d.data.value).attr('opacity', (d) => (d.value ? '1' : '0'));
    } else {
      textElements
        .text((d) => d.data.percentageStr || '')
        .call((text) =>
          text
            .append('tspan')
            .attr('fill', (d) => (colors as any)[d.data.color][300])
            .attr('font-size', '0.8em')
            .attr('opacity', (d) => (d.value ? '1' : '0'))
            .text('%')
        );
    }
  };

  updateChart();
});

function onClickOutside(val: any) {
  selected.value = undefined;
  unhighlightPieSection?.();
}
</script>

<style scoped></style>
